<script setup>
import DOMPurify from 'dompurify';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { helpers } from '@shop/frontend/web/js/helpers';
import { ref, computed } from 'vue';
import dayjs from '@frontend/web/src/js/utils/dayjs';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isShort: {
    type: Boolean,
    default: false,
  },
  isPerfomance: {
    type: Boolean,
    default: false,
  },
  isPackage: {
    type: Boolean,
    default: false,
  },
  cartCount: {
    type: Number,
    default: 0,
  },
});

const showDescription = ref(false);

const matchesInfoText = computed(() => {
  const matchesCorrectForm = helpers.wordEnding(props.event.eventsCount, [
    translator.t('event', 'match'),
    translator.t('event', 'matches'),
    translator.t('event', 'matches '),
  ]);

  return `${translator.t('event', 'Top')} ${props.event.eventsCount} ${matchesCorrectForm}`;
});

const sanitizedEventDescription = computed(() => DOMPurify.sanitize(props.event.description));
</script>

<template>
  <div
    class="i-event-item"
    :class="{
      'i-event-item_short': isShort,
      'i-event-item-info_perfomance': isPerfomance,
      'i-event-item_soon': event.settings && event.settings.isSoon,
    }"
  >
    <div
      class="i-event-item__wrapper"
      :class="{ 'i-event-item__wrapper_perfomance': isPerfomance }"
    >
      <slot name="name">
        <component
          :is="!event.settings.isSoon ? 'a' : 'div'"
          v-if="event.participants && event.participants.singular"
          v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}"
          class="i-event-item-logo-name"
        >
          <span class="i-event-item__name">
            {{ event.participants.singular.name }}
          </span>
        </component>
        <component
          :is="!event.settings.isSoon ? 'a' : 'div'"
          v-else-if="isPackage"
          v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}"
          class="i-event-item-logo-name"
        >
          <div class="i-event-item__package-name-wrapper">
            <span class="i-event-item__name_package">
              {{ event.name }}
            </span>
            <span v-if="event?.nomName" class="i-event-item__nom-name">
              {{ event.nomName }}
            </span>
          </div>
        </component>
      </slot>

      <div
        v-if="!isShort"
        class="i-event-item-info"
        :class="{ 'i-event-item-info_perfomance': isPerfomance }"
      >
        <slot name="info">
          <div class="i-event-item__container">
            <span v-if="event?.nomName" class="i-event-item__nom-name">
              {{ event.nomName }}
            </span>
            <span v-else-if="event?.nom?.name" class="i-event-item__nom-name">
              {{ event?.nom?.name }}
            </span>
            <div v-if="event.settings.isShowDate && !isPackage" class="i-event-item__date">
              <span class="text_uppercase">
                {{ dayjs(event.startTimestampStr).format("D MMM YYYY / dd") }}
              </span>
              <span class="text_uppercase">
                {{ dayjs(event.startTimestampStr).format('HH:mm') }}
              </span>
            </div>
          </div>
        </slot>
        <div class="i-event-item-actions">
          <slot name="actions">
            <a
              v-if="!(event.settings && event.settings.isSoon)" :href="event.pageLink"
              :title="translator.t('main', 'Go to the event page')"
              class="js-hover-out i-event-item__btn"
            >
              <span>
                {{ translator.t("main", "Buy a ticket") }}
              </span>
              <svg width="16" height="16">
                <use xlink:href="#goBuy" />
              </svg>
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>
            <div
              v-if="(event.settings && event.settings.isSoon)"
              class="i-event-item__btn i-event-item__btn_soon"
            >
              {{ translator.t("main", "Coming soon") }}
            </div>
          </slot>
        </div>
      </div>
    </div>
    <a
      v-if="cartCount > 0"
      href="/orders/cart" class="events__cart-mobile js-events-cart-mobile"
    >
      <svg
        width="24" height="24"
        aria-hidden="true"
        focusable="false"
      >
        <use xlink:href="#cart" />
      </svg>
      <span>{{ cartCount }}</span>
    </a>
    <div
      v-if="event.description && showDescription"
      class="i-event-item-expand"
      :class="{ 'd-block': showDescription }"
      v-html="sanitizedEventDescription"
    />
  </div>
</template>

<style lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
.i-event-item {
  border-radius: 10px;
  width: calc(50% - 65px);
  flex-shrink: 0;
  box-sizing: content-box;
  padding: 16px 24px;
  border-radius: 16px;
  background: $darker-light-gray;
  background-image: url(../../../../images/site/tickets-item-bg.svg);
  background-position: center right -50px;
  background-repeat: no-repeat;
  border: none;
  position: relative;

  @media screen and (max-width: $screen-md) {
    width: calc(100% - 30px);
    padding: 16px;
  }

  &__wrapper {
    flex-direction: column;
    align-items: flex-start;
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 50%;
  }

  &__nom-name {
    color: $color !important;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  &__btn {
    display: flex;
    min-width: 200px;
    font-size: 18px;
    font-weight: 700;
    align-items: center;
    color: $primary;
    font-family: "Roboto Condensed";
    font-size: 18px;
    font-style: normal;
    justify-content: space-between;
    font-weight: 700;
    line-height: 18px;
    border: 2px solid $primary;
    border-radius: $border-radius;
    padding: 12px;

    &:hover {
      color: $white;
      background-color: $primary;

      svg {
        stroke: $white;
      }
    }

    svg {
      stroke: $primary;
    }

    &_soon {
      background-color: $brown;
      color: $white;
      border: none;
      justify-content: center;

      &:hover {
        background-color: $brown;
      }
    }
  }

  &-info {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    max-width: 100% !important;
    width: 100%;
  }

  &__name {
    position: static !important;
    transform: none !important;
    width: 100% !important;
    margin-left: 0 !important;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    margin-bottom: 20px;

    @media screen and (max-width:$screen-md) {
      font-size: 16px !important;
    }
  }

  &__date {
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin-right: 65px;
    font-size: 18px !important;
  }

  &_soon {
    border-top: none;
    border-radius: 0 0 20px 20px;
  }

  &__soon-block {
    text-align: center;
    border-radius: 15px 15px 0 0;
    padding: 3px 0;
    width: 100%;
    font-weight: 500;
    font-size: 16px;
  }

  &__package-name-wrapper {
    display: flex;
    flex-direction: column;
    margin-left: 12px;
  }

  &__name_package {
    font-size: 18px;
    font-weight: 700;
  }

  @media (max-width: $screen-xs) {
    margin-bottom: 0;

    &__wrapper {
      flex-wrap: wrap;
    }

    &__container {
      width: 100%;
    }
  }
}

.i-event-item-logo-name {
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.i-event-item-logo-name .i-event-item__name {
  top: 50%;
  transform: translateY(-50%);
}

.i-event-item-info {
  margin: 0;
  width: 100%;

  @media screen and (max-width: $screen-md) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  &__matches {
    color: $white;
    background-color: $background-second;
  }
}
</style>
